import React from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Avatar,
  Grid,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as VerifiedIcon,
  EmojiEvents as TrophyIcon,
  CalendarToday as CalendarIcon,
  RocketLaunch as RocketIcon,
} from "@mui/icons-material";
import moment from "moment";
import { colors, borderRadiusPx } from "../../../../../theme";
import { Path, DEFAULT_CURRENCY, LOTTERY_NAMES } from "../../../../../static/Constants";
import { CommonChip, CommonButton } from "../../../../Components/ui";
import AutoLogo from "../../../../../static/Images/logo.png";

const MissileHeader = ({
  lotteryId,
  lottery,
  datelist,
  partnerDetails,
  partnerLogoUrl,
  currency = DEFAULT_CURRENCY,
  totalPrizeFormatted = "0",
  tierInfo,
}) => {
  const history = useHistory();

  const isRollover = lottery?.status === "5";
  const isConcluded =
    lottery?.status !== "0" && lottery?.status !== "5" && lottery?.status !== undefined;

  const partnerName =
    partnerDetails?.name ||
    partnerDetails?.companyName ||
    "AutoBET Official";

  const formattedStartTime = datelist?.startTime
    ? moment.unix(Number(datelist.startTime)).format("DD MMM YYYY, hh:mm A")
    : null;

  const formattedEndTime = datelist?.endTime
    ? moment.unix(Number(datelist.endTime)).format("DD MMM YYYY, hh:mm A")
    : null;

  return (
    <Box
      sx={{
        bgcolor: colors.slate[900],
        borderRadius: borderRadiusPx.xxl,
        p: { xs: 2.5, sm: 3.5, md: 4 },
        mb: 3.5,
        color: "#ffffff",
        position: "relative",
        overflow: "hidden",
        border: `1px solid ${colors.slate[800]}`,
        boxShadow: "0 12px 32px rgba(15, 23, 42, 0.35)",
      }}
    >
      {/* Background Subtle Accent Glow */}
      <Box
        sx={{
          position: "absolute",
          top: "-40%",
          right: "-10%",
          width: "420px",
          height: "420px",
          borderRadius: borderRadiusPx.circle,
          background: `radial-gradient(circle, ${colors.primary.main}22 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      {/* Top Bar inside Card: Badges and Back Action */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 2.5 }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
          {isRollover ? (
            <CommonChip
              label="Rollover Stage"
              variant="warning"
              size="small"
              sx={{ fontWeight: 700 }}
            />
          ) : isConcluded ? (
            <CommonChip
              label="Concluded"
              variant="neutral"
              size="small"
              sx={{ fontWeight: 700 }}
            />
          ) : (
            <CommonChip
              label="Live Lottery"
              variant="success"
              size="small"
              sx={{ fontWeight: 700 }}
            />
          )}

          {tierInfo?.badge && (
            <CommonChip
              label={tierInfo.badge}
              variant="primary"
              size="small"
              icon={<RocketIcon sx={{ fontSize: "14px !important" }} />}
              sx={{ fontWeight: 700 }}
            />
          )}
        </Stack>

        <CommonButton
          variant="secondary"
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={() => history.push(Path.allmissilelottery)}
          label="All Rockets"
          sx={{
            bgcolor: colors.slate[800],
            borderColor: colors.slate[700],
            color: colors.slate[200],
            "&:hover": {
              bgcolor: colors.slate[700],
            },
          }}
        />
      </Stack>

      {/* Main Header Content Grid */}
      <Grid container spacing={3} alignItems="center">
        {/* Left Side: Title, Description, Partner */}
        <Grid item xs={12} md={7}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.02em",
              fontSize: { xs: "1.75rem", sm: "2.15rem", md: "2.4rem" },
              color: "#ffffff",
              mb: 1,
            }}
          >
            {`${LOTTERY_NAMES.ROCKETBET} Lottery #${lotteryId}`}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: colors.slate[300],
              maxWidth: "520px",
              lineHeight: 1.6,
              mb: 2.5,
            }}
          >
            Predict the correct target sequence to launch your missile and win the
            on-chain prize pool.
          </Typography>

          {/* Organizer / Partner Identity Badge */}
          {/* <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.06)",
              borderRadius: borderRadiusPx.lg,
              p: 1.25,
              width: "fit-content",
              border: `1px solid ${colors.slate[800]}`,
            }}
          >
            <Avatar
              src={partnerLogoUrl || AutoLogo}
              alt={partnerName}
              sx={{
                width: 32,
                height: 32,
                bgcolor: colors.slate[800],
                border: `1px solid ${colors.slate[700]}`,
              }}
            />
            <Box>
              <Typography
                variant="caption"
                sx={{ color: colors.slate[400], display: "block", lineHeight: 1 }}
              >
                Organized by
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: "#ffffff", lineHeight: 1.3 }}
                >
                  {partnerName}
                </Typography>
                <VerifiedIcon sx={{ fontSize: 14, color: colors.primary.main }} />
              </Stack>
            </Box>
          </Stack> */}
        </Grid>

        {/* Right Side: Prize Pool & Timeline Card */}
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.05)",
              border: `1px solid ${colors.slate[700]}`,
              borderRadius: borderRadiusPx.xl,
              p: { xs: 2, sm: 2.5 },
              backdropFilter: "blur(12px)",
            }}
          >
            {/* Prize Pool Callout */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <Avatar
                sx={{
                  bgcolor: colors.amber[500],
                  color: colors.slate[900],
                  width: 44,
                  height: 44,
                }}
              >
                <TrophyIcon />
              </Avatar>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.slate[300],
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    fontWeight: 600,
                  }}
                >
                  Estimated Prize Pool
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    color: colors.amber[400],
                    letterSpacing: "-0.01em",
                  }}
                >
                  {totalPrizeFormatted} {currency}
                </Typography>
              </Box>
            </Stack>

            {/* Dates / Timeline */}
            <Stack spacing={1} sx={{ pt: 1.5, borderTop: `1px solid ${colors.slate[800]}` }}>
              {formattedStartTime && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CalendarIcon sx={{ fontSize: 16, color: colors.slate[400] }} />
                  <Typography variant="caption" sx={{ color: colors.slate[300] }}>
                    <strong>Starts:</strong> {formattedStartTime}
                  </Typography>
                </Stack>
              )}
              {formattedEndTime && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CalendarIcon sx={{ fontSize: 16, color: colors.slate[400] }} />
                  <Typography variant="caption" sx={{ color: colors.slate[300] }}>
                    <strong>Ends:</strong> {formattedEndTime}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MissileHeader;
